import { isBefore, startOfDay } from 'date-fns';
import { z } from 'zod';

import { parseCalendarDate } from '@/utils/date-formatters';

import { BillDetailsItemType, BillTimeOffSourceType } from '../types';
import {
  BillLineValidationIssueCode,
  BillValidationIssueCode,
  getBillLineValidationIssues,
  getBillValidationIssues,
} from './bill-item-validation';

const REQUIRED_MESSAGE = 'authenticated.bills:details.edit.validation.required';
const DUE_DATE_BEFORE_PERIOD_END_MESSAGE =
  'authenticated.bills:details.edit.validation.dueDateBeforePeriodEnd';

const SUPPORTED_BILL_ITEM_TYPES: BillDetailsItemType[] = [
  BillDetailsItemType.Project,
  BillDetailsItemType.TimeOffType,
  BillDetailsItemType.Overhead,
];

export const UPDATE_BILL_LINE_MESSAGE_KEY_BY_ISSUE_CODE: Record<
  BillLineValidationIssueCode,
  string
> = {
  [BillLineValidationIssueCode.MissingProject]:
    'authenticated.bills:details.edit.validation.projectRequired',
  [BillLineValidationIssueCode.MissingTimeOffType]:
    'authenticated.bills:details.edit.validation.timeOffTypeRequired',
  [BillLineValidationIssueCode.InvalidQuantity]:
    'authenticated.bills:details.edit.validation.invalidHoursOrRate',
  [BillLineValidationIssueCode.InvalidUnitPrice]:
    'authenticated.bills:details.edit.validation.invalidHoursOrRate',
  [BillLineValidationIssueCode.ZeroCalculatedLine]:
    'authenticated.bills:details.edit.validation.zeroAmountLineError',
  [BillLineValidationIssueCode.EmptyDescription]:
    'authenticated.bills:details.edit.validation.descriptionRequired',
  [BillLineValidationIssueCode.DescriptionTooLong]:
    'authenticated.bills:details.edit.validation.descriptionTooLong',
  [BillLineValidationIssueCode.InvalidAmount]:
    'authenticated.bills:details.edit.validation.invalidAmount',
};

export const UPDATE_BILL_MESSAGE_BY_ISSUE: Record<BillValidationIssueCode, string> = {
  [BillValidationIssueCode.EmptyItems]: 'authenticated.bills:details.edit.validation.emptyItems',
  [BillValidationIssueCode.NegativeTotal]:
    'authenticated.bills:details.edit.validation.negativeTotal',
};

const itemShape = z.object({
  sourceItemId: z.number().optional(),
  entityType: z.nativeEnum(BillDetailsItemType),
  entityId: z.string(),
  description: z.string(),
  quantity: z.string(),
  unitPrice: z.string(),
  amount: z.string(),
  timeOffSourceType: z.nativeEnum(BillTimeOffSourceType).optional(),
});

const itemSchema = itemShape.superRefine((item, context) => {
  if (!SUPPORTED_BILL_ITEM_TYPES.includes(item.entityType)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['entityType'],
      message: 'authenticated.bills:details.edit.validation.unsupportedItem',
    });
  }

  getBillLineValidationIssues(item).forEach(issue => {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: [issue.field],
      message: UPDATE_BILL_LINE_MESSAGE_KEY_BY_ISSUE_CODE[issue.code],
    });
  });
});

export type UpdateBillItemFormValues = z.infer<typeof itemSchema>;

export const updateBillValidationSchema = z
  .object({
    monthlySalaryEnabled: z.boolean(),
    period: z
      .object({ from: z.date().optional(), to: z.date().optional() })
      .refine(value => value.from && value.to, { message: REQUIRED_MESSAGE })
      .refine(value => !value.from || !value.to || value.from <= value.to, {
        message: 'authenticated.bills:details.edit.validation.period',
      }),
    dueDate: z.date({ required_error: REQUIRED_MESSAGE }).optional(),
    currencyCode: z.string().regex(/^[A-Z]{3}$/, REQUIRED_MESSAGE),
    items: z.array(itemSchema),
  })
  .superRefine((values, context) => {
    if (!values.dueDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dueDate'],
        message: REQUIRED_MESSAGE,
      });
    }

    // The due date is the picker's local day; the period end stays the range picker's UTC midnight.
    if (
      values.dueDate &&
      values.period.to &&
      isBefore(startOfDay(values.dueDate), parseCalendarDate(values.period.to))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dueDate'],
        message: DUE_DATE_BEFORE_PERIOD_END_MESSAGE,
      });
    }

    getBillValidationIssues(values).forEach(issue => {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items'],
        message: UPDATE_BILL_MESSAGE_BY_ISSUE[issue],
      });
    });
  });

export type UpdateBillFormValues = z.infer<typeof updateBillValidationSchema>;
